import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';

const { FiLock, FiUsers, FiHeart, FiDollarSign, FiCheck, FiShield, FiClock, FiArrowRight, FiGift } = FiIcons;

const WhyInviteOnly = () => {
  const handleCTAClick = () => {
    window.open('https://register.yiksy.com/', '_blank');
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section className="bg-gradient-to-br from-gray-50 via-white to-blue-50 py-16 sm:py-20 lg:py-28 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-blue-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30 translate-x-1/3 -translate-y-1/3"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30 -translate-x-1/3 translate-y-1/3"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <SafeIcon icon={FiLock} className="mr-2" />
            Exclusive Access
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-6">
            Why Invite-Only?
          </h2>
          <p className="text-lg sm:text-xl text-text-light max-w-3xl mx-auto">
            We're keeping access limited — and here's why:
          </p>
        </motion.div>

        <motion.div 
          className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 overflow-hidden"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Three main reasons */}
          <motion.div 
            className="divide-y divide-gray-100"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {[
              {
                icon: FiUsers,
                number: "1",
                title: "Personal Support from Our Team",
                description: "Our agency team is working closely with every beta user to ensure you get the most from the platform.",
                color: "text-blue-600",
                bgColor: "bg-blue-50"
              },
              {
                icon: FiDollarSign,
                number: "2",
                title: "A Locked-In Early Price",
                description: "Join now and secure a fixed, discounted rate that stays the same even after launch.",
                color: "text-green-600",
                bgColor: "bg-green-50"
              },
              {
                icon: FiHeart,
                number: "3",
                title: "Shaping the Future Together",
                description: "We're refining the platform with real feedback from early users like you.",
                color: "text-purple-600",
                bgColor: "bg-purple-50"
              }
            ].map((reason, index) => (
              <motion.div
                key={index}
                className="p-8 sm:p-10 hover:bg-gray-50/50 transition-colors"
                variants={item}
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className={`w-16 h-16 ${reason.bgColor} rounded-2xl flex items-center justify-center relative`}>
                      <SafeIcon icon={reason.icon} className={`${reason.color} text-2xl`} />
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white font-bold text-sm">{reason.number}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-text mb-3">
                      {reason.title}
                    </h3>
                    <p className="text-text-light leading-relaxed text-base sm:text-lg">
                      {reason.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Offer section */}
          <motion.div 
            className="bg-gradient-to-r from-blue-50 via-white to-purple-50 p-8 sm:p-12 border-t border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full text-lg font-bold mb-6 shadow-lg">
                <SafeIcon icon={FiGift} className="mr-2" />
                That's why we're offering
              </div>
              <h3 className="text-3xl sm:text-4xl font-bold text-text mb-2">
                <span className="text-primary">15 days</span> of full access
              </h3>
              <p className="text-lg text-text-light">Free, no credit card required</p>
            </div>

            {/* Two outcome boxes */}
            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 border border-green-200 shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <SafeIcon icon={FiCheck} className="text-green-600 text-xl" />
                  </div>
                  <h4 className="font-bold text-text">If it's a fit</h4>
                </div>
                <p className="text-text-light">You keep your spot at this special rate.</p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <SafeIcon icon={FiShield} className="text-blue-600 text-xl" />
                  </div>
                  <h4 className="font-bold text-text">If not</h4>
                </div>
                <p className="text-text-light">No worries at all.</p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <motion.button 
                className="bg-primary text-white px-10 py-4 rounded-xl text-lg font-bold hover:bg-blue-700 transition-all shadow-lg inline-flex items-center group touch-manipulation"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCTAClick}
              >
                Build My Free Profile
                <SafeIcon icon={FiArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              <p className="text-text-light text-sm mt-4">
                15-day free trial • No credit card required • Cancel anytime
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyInviteOnly;